import { Sel, STag } from '#~/stag/tag.js';
import { Loading } from './loading.jsx';
import type { ModelProps } from './types.js';
import { useConfirmMakeModel } from './utils.js';
import { DriverStatusBadge, useDriverStatus } from '#~/device/components/driver-status/index.js';
import * as Select from '@studio/design-system/select';
import { castId } from '@studio/models';
import type { Model as M_Model } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isEmpty } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { memo, useMemo, useState } from 'react';

/** Model item with driver status badge. */
const ModelItem = ({ model }: { model: M_Model }): ReactElement => {
  const driverStatus = useDriverStatus(model);

  return (
    <div className='flex w-full items-center justify-between'>
      <Select.ItemText>{model.name}</Select.ItemText>
      {driverStatus && (
        <DriverStatusBadge
          status={driverStatus}
          data-stag='config.video-device.brand-model.status-badge'
        />
      )}
    </div>
  );
};

/** `<Model>` component. */
export const Model = memo(
  ({
    disabled,
    models,
    brandId,
    selection,
    size,
    confirmOther = true,
    ...props
  }: ModelProps): ReactElement => {
    const confirmMakeModel = useConfirmMakeModel();
    const [magicNumber, setMagicNumber] = useState(0);

    const handleValueChange = useLatestFn((nextValue: string) => {
      const prevModel = selection.value;
      const nextModel = castId<M_Model['id']>(nextValue);

      if (nextModel === selection.value) {
        return;
      }

      const model = models?.items[nextModel];
      if (confirmOther && model?.name?.toLocaleLowerCase().includes('other')) {
        confirmMakeModel({
          onOkay: () => selection(nextModel),
          onCancel: () => {
            selection(prevModel ?? 'none');
            setMagicNumber((µ) => µ + 1);
          }
        });
      } else {
        selection(nextModel);
      }
    });

    return (
      <Select.Root
        key={`model-select-gen-${magicNumber}`}
        disabled={disabled}
        onValueChange={handleValueChange}
        size={size}
        value={selection.value}
      >
        <Select.Trigger {...props} {...STag(Sel.ConfigVideoDeviceBrandModel, 'model')}>
          <Select.Value
            placeholder={
              disabled ? (
                'Select a brand'
              ) : isEmpty(models) && brandId ? (
                <Loading />
              ) : (
                'Select a model'
              )
            }
          />
        </Select.Trigger>

        <Select.Content>
          {useMemo(() => {
            let other: T_Maybe<M_Model>;

            const items = Object.values(models?.items ?? []).filter((µ) => {
              if (µ.name.toLowerCase().includes('other')) {
                other = µ;

                return false;
              }

              return true;
            });

            return (
              <>
                {items.map((µ) => (
                  <Select.Item
                    key={µ.id}
                    value={µ.id}
                    {...STag(Sel.ConfigVideoDeviceBrandModel, 'model-list', µ.id)}
                  >
                    {/* {µ.name} */}
                    <ModelItem model={µ} />
                  </Select.Item>
                ))}

                {!!other && (
                  <Select.Item
                    key={other.id}
                    value={other.id}
                    {...STag(Sel.ConfigVideoDeviceBrandModel, 'model-list', other.id)}
                  >
                    {/* {other.name} */}
                    <ModelItem model={other} />
                  </Select.Item>
                )}
              </>
            );
          }, [models])}
        </Select.Content>
      </Select.Root>
    );
  },
  (prev, next) => prev.models === next.models && prev.selection === next.selection
);
